import { FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from '@/components/ui/form';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Info } from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { UseFormReturn } from 'react-hook-form';

interface ManualRenewalOverrideProps {
  form: UseFormReturn<any>;
  customerId: string;
  availableTransactions: any[];
  isDetectedAsRenewal: boolean;
}

export function ManualRenewalOverride({
  form,
  customerId,
  availableTransactions,
  isDetectedAsRenewal,
}: ManualRenewalOverrideProps) {
  const forceAsRenewal = form.watch('force_as_renewal');

  // Filtrar apenas transações do cliente selecionado
  const customerTransactions = availableTransactions.filter(
    (tx) => tx.customer_saas_id === customerId
  );

  return (
    <div className="space-y-4 border-t pt-4">
      <Alert>
        <Info className="h-4 w-4" />
        <AlertDescription>
          <strong>Casos especiais:</strong> Use esta opção apenas quando a detecção automática
          não capturar corretamente a renovação (ex: migração de sistema, mudança de plano).
        </AlertDescription>
      </Alert>

      <FormField
        control={form.control}
        name="force_as_renewal"
        render={({ field }) => (
          <FormItem className="flex flex-row items-start space-x-3 space-y-0">
            <FormControl>
              <Checkbox
                checked={field.value}
                onCheckedChange={field.onChange}
                disabled={isDetectedAsRenewal}
              />
            </FormControl>
            <div className="space-y-1 leading-none">
              <FormLabel>
                Forçar como renovação manualmente
              </FormLabel>
              <FormDescription>
                {isDetectedAsRenewal
                  ? '✅ Sistema já detectou esta transação como renovação'
                  : 'Marque se esta transação for uma renovação não detectada automaticamente'}
              </FormDescription>
            </div>
          </FormItem>
        )}
      />

      {forceAsRenewal && !isDetectedAsRenewal && (
        <FormField
          control={form.control}
          name="manual_previous_transaction_id"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Transação Anterior (Referência) *</FormLabel>
              <Select onValueChange={field.onChange} value={field.value}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione a transação anterior" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {customerTransactions.map((tx) => (
                    <SelectItem key={tx.id} value={tx.id}>
                      {format(new Date(tx.payment_date), 'dd/MM/yyyy', { locale: ptBR })} -{' '}
                      {new Intl.NumberFormat('pt-BR', {
                        style: 'currency',
                        currency: tx.currency,
                      }).format(tx.amount_paid)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormDescription>
                Selecione a transação que esta renovação está substituindo
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
      )}
    </div>
  );
}
